import type { Point } from '@ardoq/math';

export type PathPiece =
  | { type: 'line'; from: Point; to: Point }
  | { type: 'curve'; from: Point; control: Point; to: Point };

export type AnchoredPiece = { piece: PathPiece; dashOffset: number };

/** The canvas repeats an odd-length dash array to make it even. */
const dashPeriod = (dashArray: number[]): number => {
  const sum = dashArray.reduce((total, length) => total + length, 0);
  return dashArray.length % 2 ? sum * 2 : sum;
};

const isBefore = ([ax, ay]: Point, [bx, by]: Point): boolean =>
  ax < bx || (ax === bx && ay < by);

const reversePiece = (piece: PathPiece): PathPiece =>
  piece.type === 'line'
    ? { type: 'line', from: piece.to, to: piece.from }
    : { type: 'curve', from: piece.to, control: piece.control, to: piece.from };

const toCanonicalDirection = (piece: PathPiece): PathPiece =>
  isBefore(piece.to, piece.from) ? reversePiece(piece) : piece;

const hasLength = ({ from, to }: PathPiece): boolean =>
  from[0] !== to[0] || from[1] !== to[1];

/** Where `from` lies along the infinite line through `from` and `to`, measured from the origin. */
const positionAlongLine = (
  [fromX, fromY]: Point,
  [toX, toY]: Point
): number => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  return (fromX * dx + fromY * dy) / Math.hypot(dx, dy);
};

const modulo = (value: number, period: number): number =>
  ((value % period) + period) % period;

/**
 * Anchors the dash pattern of each piece to the canvas instead of to the start
 * of its path: every piece is drawn in a fixed direction, with a dash offset
 * taken from its position along its own line. Pieces of different paths that
 * overlap on the same line then paint identical dashes, so overlapping dashed
 * edges stay dashed rather than filling in each other's gaps.
 */
export const anchorDashes = (
  pieces: PathPiece[],
  dashArray: number[]
): AnchoredPiece[] => {
  const period = dashPeriod(dashArray);
  return pieces
    .filter(hasLength)
    .map(toCanonicalDirection)
    .map(piece => ({
      piece,
      dashOffset: modulo(positionAlongLine(piece.from, piece.to), period),
    }));
};
